import { Nullable } from "common/types";

export const MESSAGES_PARAM = "messages";
export const MESSAGES_PAGE_PARAM = "messages_page";
export const MESSAGES_PER_PAGE_PARAM = "messages_per_page";
export const MESSAGES_OPEN = "open";
export const MESSAGES_PER_PAGE = 20;

export const PANEL_PARAMS = [MESSAGES_PARAM, MESSAGES_PAGE_PARAM, MESSAGES_PER_PAGE_PARAM];

/** `navigate` accepts a partial target, so omitting `pathname` keeps the page the user is on. */
export type MessagesNavigate = (to: { search: string }, options?: { replace?: boolean }) => void;

let messageNavigate: Nullable<MessagesNavigate> = null;

/**
 * Held for the new-message toast, which is raised with no component on the stack.
 *
 * @param navigate
 */
export const setMessageNavigate = (navigate: Nullable<MessagesNavigate>): void => {
  messageNavigate = navigate;
};

export const getMessageNavigate = (): Nullable<MessagesNavigate> => messageNavigate;

const positiveInt = (value: Nullable<string>): Nullable<number> => {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const messagesPanelOpen = (params: URLSearchParams): boolean => params.has(MESSAGES_PARAM);

/**
 * The message being read, or `null` when the panel is open without one expanded.
 *
 * @param params
 * @returns
 */
export const expandedMessageId = (params: URLSearchParams): Nullable<string> => {
  const value = params.get(MESSAGES_PARAM);

  return !value || value === MESSAGES_OPEN ? null : value;
};

export const messagesPage = (params: URLSearchParams): number => positiveInt(params.get(MESSAGES_PAGE_PARAM)) ?? 1;

export const messagesPerPage = (params: URLSearchParams): number =>
  positiveInt(params.get(MESSAGES_PER_PAGE_PARAM)) ?? MESSAGES_PER_PAGE;

export const hasNonMessagesParams = (params: URLSearchParams): boolean =>
  Array.from(params.keys()).some((key) => !PANEL_PARAMS.includes(key));

/**
 * Carries the panel's parameters onto a target a loader is redirecting to, so a redirect decided by
 * the host page doesn't close the panel the user reloaded with open.
 *
 * @param target mutated in place
 * @param source
 * @returns
 */
export const copyPanelParams = (target: URLSearchParams, source: URLSearchParams): URLSearchParams => {
  PANEL_PARAMS.forEach((key) => {
    const value = source.get(key);
    if (value !== null) target.set(key, value);
  });

  return target;
};

/**
 * The search string for the current page with the panel's state replaced.
 *
 * @param search
 * @param value `MESSAGES_OPEN`, a message id to expand, or `null` to close the panel
 * @param options `resetPage` drops the page parameter, so the target message is on the page fetched
 * @returns
 */
export const messagesSearch = (search: string, value: string | null, options: { resetPage?: boolean } = {}): string => {
  const params = new URLSearchParams(search);

  if (value === null) {
    PANEL_PARAMS.forEach((key) => params.delete(key));
  } else {
    params.set(MESSAGES_PARAM, value);
    if (options.resetPage) params.delete(MESSAGES_PAGE_PARAM);
  }

  const next = params.toString();

  return next ? `?${next}` : "";
};

const withoutPanelParams = (url: URL): string => {
  const params = new URLSearchParams(url.search);
  PANEL_PARAMS.forEach((key) => params.delete(key));
  params.sort();

  return `${url.pathname}?${params.toString()}`;
};

export const onlyMessagesParamsChanged = (currentUrl: URL, nextUrl: URL): boolean =>
  currentUrl.search !== nextUrl.search && withoutPanelParams(currentUrl) === withoutPanelParams(nextUrl);
